import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { cardSchema } from "./cards";
import { apiGet, apiPost } from "./client";

/**
 * /api/v2/sync/*, /api/v2/jobs* — sozlesme §5 (backend/app/core/api/sync.py).
 * touch yaniti: app/core/dispatcher.py `touch()` — {enqueued, cooldown,
 * auth_blocked, last_success_at}.
 */

export const sourceStateSchema = z.object({
  source: z.string(),
  watermark_ts: z.string().nullable(),
  last_success_at: z.string().nullable(),
  last_attempt_at: z.string().nullable(),
  last_error: z.string().nullable(),
  backfill_done: z.boolean(),
  interval_min: z.number(),
});
export type SourceStateOut = z.infer<typeof sourceStateSchema>;

export const syncStatusResponseSchema = z.object({
  sources: z.array(sourceStateSchema),
  cards: z.array(cardSchema),
});
export type SyncStatusResponse = z.infer<typeof syncStatusResponseSchema>;

export const touchResponseSchema = z.object({
  enqueued: z.array(z.string()),
  cooldown: z.array(z.string()),
  auth_blocked: z.array(z.string()),
  last_success_at: z.record(z.string(), z.string().nullable()),
});
export type TouchResponse = z.infer<typeof touchResponseSchema>;

export const jobSchema = z
  .object({
    id: z.number(),
    kind: z.string(),
    status: z.string(),
  })
  .passthrough();
export type JobOut = z.infer<typeof jobSchema>;

export const jobsResponseSchema = z.object({ jobs: z.array(jobSchema) });

export const okJobResponseSchema = z.object({ ok: z.boolean() });

export function fetchSyncStatus(): Promise<SyncStatusResponse> {
  return apiGet("/api/v2/sync/status", syncStatusResponseSchema);
}

export function touchSyncRequest(): Promise<TouchResponse> {
  return apiPost("/api/v2/sync/touch", touchResponseSchema);
}

export function fetchJobs(limit = 50) {
  return apiGet(`/api/v2/jobs?limit=${limit}`, jobsResponseSchema);
}

export function cancelJobRequest(jobId: number) {
  return apiPost(`/api/v2/jobs/${jobId}/cancel`, okJobResponseSchema);
}

// ---------------------------------------------------------------------------
// Kancalar — refresh.js'in gorunurluk korumali yenileme deseni: sekme arka
// planda iken tazeleme durur (refetchIntervalInBackground:false), sekme
// odaklandiginda tekrar sorgular (refetchOnWindowFocus:true).
// ---------------------------------------------------------------------------

export const SYNC_STATUS_QUERY_KEY = ["sync", "status"] as const;

export function useSyncStatusQuery() {
  return useQuery({
    queryKey: SYNC_STATUS_QUERY_KEY,
    queryFn: fetchSyncStatus,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
}

export function useTouchMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: touchSyncRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["cards"] });
    },
  });
}

export function useJobsQuery(limit = 50) {
  return useQuery({ queryKey: ["jobs", limit], queryFn: () => fetchJobs(limit) });
}

export function useCancelJobMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelJobRequest,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["jobs"] }),
  });
}
